// AboneliklerSayfasi.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Autocomplete,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  IconButton,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CancelIcon from "@mui/icons-material/Cancel";

import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const SubscriptionPage = () => {
  const [open, setOpen] = useState(false);
  const [selectedSubDoc, setSelectedSubDoc] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  const [abonelikler, setAbonelikler] = useState([]);
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Quota panel
  const [quotaItems, setQuotaItems] = useState([]);
  const [quotaOriginal, setQuotaOriginal] = useState([]);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [hasQuotaChanges, setHasQuotaChanges] = useState(false);

  const [formData, setFormData] = useState({
    userId: "",
    fullName: "",
    subscriptions: [
      {
        packageId: "",
        packageName: "",
        startDate: new Date().toISOString().split("T")[0],
        endDate: "",
        isActive: true,
      },
    ],
    isActive: false,
    _id: null,
  });

  // ========= AUTH =========
  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("token");

    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  // ========================

  // ========= HELPERS =========
  const numeric = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

  const quotaLabel = (t) => {
    if (t === "MESSAGE") return "Mesaj";
    if (t === "WEB_SEARCH") return "Web";
    if (t === "VOICE") return "Ses";
    return t || "—";
  };

  const deepClone = (x) => JSON.parse(JSON.stringify(x));

  const computeRemainingText = (row) => {
    if (row.limitType === "COUNT") {
      const rem = Math.max(0, numeric(row.limitValue) - numeric(row.usedCount));
      return `${rem} adet`;
    }
    const totalSec = numeric(row.limitValue) * 60;
    const remSec = Math.max(0, totalSec - numeric(row.usedSeconds));
    const mm = String(Math.floor(remSec / 60)).padStart(2, "0");
    const ss = String(remSec % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const buildQuotaDiffs = (current = [], original = []) => {
    const byRowId = Object.fromEntries((original || []).map((o) => [String(o.rowId), o]));
    const diffs = [];

    for (const c of current || []) {
      const o = byRowId[String(c.rowId)];
      if (!o) continue;

      const payload = {};

      if (String(c.limitType) !== String(o.limitType)) payload.limitType = c.limitType;

      const cLimit = numeric(c.limitValue);
      const oLimit = numeric(o.limitValue);
      if (cLimit !== oLimit) payload.limitValue = cLimit;

      const cUsedCount = numeric(c.usedCount);
      const oUsedCount = numeric(o.usedCount);
      if (cUsedCount !== oUsedCount) payload.usedCount = cUsedCount;

      const cUsedSeconds = numeric(c.usedSeconds);
      const oUsedSeconds = numeric(o.usedSeconds);
      if (cUsedSeconds !== oUsedSeconds) payload.usedSeconds = cUsedSeconds;

      const cIsActive = !!c.isActive;
      const oIsActive = !!o.isActive;
      if (cIsActive !== oIsActive) payload.isActive = cIsActive;

      if (Object.keys(payload).length > 0) {
        diffs.push({
          itemId: c.subItemId,
          quotaType: c.quotaType,
          payload,
        });
      }
    }

    return diffs;
  };

  const packageBotsLabel = (pkg) => {
    const bots = Array.isArray(pkg?.bots) ? pkg.bots : [];
    const names = bots.map((b) => (typeof b === "object" ? (b.name || b._id) : b));
    if (names.length === 0) return "Bot yok";
    if (names.length <= 2) return names.join(", ");
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  };
  // ===========================

  // ========= FETCHERS =========
  const fetchUsers = useCallback(async () => {
    const res = await axios.get("/api/users", {
      headers: getAuthHeaders(),
    });
    setUsers(Array.isArray(res.data) ? res.data : []);
  }, [getAuthHeaders]);

  const fetchPackages = useCallback(async () => {
    const res = await axios.get("/api/packages", {
      headers: getAuthHeaders(),
    });
    setPackages(Array.isArray(res.data?.data) ? res.data.data : []);
  }, [getAuthHeaders]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await axios.get("/api/subscriptions", {
        headers: getAuthHeaders(),
      });
      setAbonelikler(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Abonelikleri çekme hatası:", err);
    }
  }, [getAuthHeaders]);

  const fetchQuotaItems = useCallback(async (docId) => {
    if (!docId) return [];
    setQuotaLoading(true);
    try {
      const r = await axios.get(`/api/subscriptions/${docId}/items`, {
        headers: getAuthHeaders(),
      });
      const data = Array.isArray(r.data?.data) ? r.data.data : [];

      setQuotaItems(data);
      setQuotaOriginal(deepClone(data));
      setHasQuotaChanges(false);
      return data;
    } catch (e) {
      setQuotaItems([]);
      setQuotaOriginal([]);
      setSnackbar({ open: true, message: "Kota/Sayaçlar alınamadı", severity: "error" });
      return [];
    } finally {
      setQuotaLoading(false);
    }
  }, [getAuthHeaders]);

  const resetOneQuota = useCallback(async ({ docId, itemId, quotaType }) => {
    try {
      await axios.post(
        `/api/subscriptions/${docId}/items/${itemId}/quotas/${quotaType}/reset`,
        {},
        {
          headers: getAuthHeaders(),
        }
      );
      await fetchQuotaItems(docId);
      setSnackbar({ open: true, message: "Kota sayaçları sıfırlandı", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: "Kota sayaçları sıfırlanamadı", severity: "error" });
    }
  }, [fetchQuotaItems, getAuthHeaders]);
  // ============================

  useEffect(() => {
    fetchUsers();
    fetchPackages();
    fetchSubscriptions();
  }, [fetchUsers, fetchPackages, fetchSubscriptions]);

  // ========= DIALOG HELPERS =========
  const handleClose = () => {
    setViewMode(false);
    setQuotaItems([]);
    setQuotaOriginal([]);
    setHasQuotaChanges(false);
    setSelectedSubDoc(null);

    setFormData({
      userId: "",
      fullName: "",
      subscriptions: [
        {
          packageId: "",
          packageName: "",
          startDate: new Date().toISOString().split("T")[0],
          endDate: "",
          isActive: true,
        },
      ],
      isActive: false,
      _id: null,
    });
  };

  const handleSnackbarClose = () => setSnackbar((p) => ({ ...p, open: false }));
  // ================================

  // ========= FORM HANDLERS =========
  const handleSubscriptionChange = (index, field, value) => {
    const updated = [...formData.subscriptions];
    updated[index][field] = value;

    if (field === "packageName") {
      const selectedPkg = packages.find((p) => p.name === value);
      updated[index].packageId = selectedPkg?._id || "";

      if (selectedPkg && updated[index].startDate) {
        const start = new Date(updated[index].startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + Number(selectedPkg.duration || 0));
        updated[index].endDate = end.toISOString().split("T")[0];
      }
    }

    if (field === "startDate") {
      const selectedPkg = packages.find((p) => p.name === updated[index].packageName);
      if (selectedPkg) {
        const start = new Date(value);
        const end = new Date(start);
        end.setDate(start.getDate() + Number(selectedPkg.duration || 0));
        updated[index].endDate = end.toISOString().split("T")[0];
      }
    }

    setFormData({ ...formData, subscriptions: updated });
  };

  const addSubscriptionField = () => {
    setFormData((p) => ({
      ...p,
      subscriptions: [
        ...p.subscriptions,
        {
          packageId: "",
          packageName: "",
          startDate: new Date().toISOString().split("T")[0],
          endDate: "",
          isActive: true,
        },
      ],
    }));
  };

  const removeSubscriptionField = (index) => {
    const updated = formData.subscriptions.filter((_, i) => i !== index);
    setFormData({ ...formData, subscriptions: updated });
  };

  const handleToggleStatus = async (row) => {
    try {
      await axios.put(
        `/api/subscriptions/${row._id}`,
        { ...row, isActive: !row.isActive },
        { headers: getAuthHeaders() }
      );
      setSnackbar({
        open: true,
        message: `Abonelik ${row.isActive ? "pasifleştirildi" : "aktifleştirildi"}!`,
        severity: "info",
      });
      fetchSubscriptions();
    } catch (err) {
      console.error("Durum değiştirme hatası:", err);
      setSnackbar({ open: true, message: "Durum güncellenemedi!", severity: "error" });
    }
  };

  const handleEdit = (row) => {
    setFormData({
      userId: row.userId,
      fullName: row.fullName,
      subscriptions: (row.subscriptions || []).map((sub) => ({
        ...sub,
        startDate: sub.startDate?.split("T")[0],
        endDate: sub.endDate?.split("T")[0],
      })),
      isActive: row.isActive,
      _id: row._id,
    });

    setOpen(true);
    setViewMode(false);
    setSelectedSubDoc(row);
    fetchQuotaItems(row._id);
  };

  const handleView = (row) => {
    setFormData({
      userId: row.userId,
      fullName: row.fullName,
      subscriptions: (row.subscriptions || []).map((sub) => ({
        ...sub,
        startDate: sub.startDate?.split("T")[0],
        endDate: sub.endDate?.split("T")[0],
      })),
      isActive: row.isActive,
      _id: row._id,
    });

    setOpen(true);
    setViewMode(true);
    setSelectedSubDoc(row);
    fetchQuotaItems(row._id);
  };

  const handleDelete = async (row) => {
    try {
      await axios.delete(`/api/subscriptions/${row._id}`, {
        headers: getAuthHeaders(),
      });
      setSnackbar({ open: true, message: "Abonelik silindi!", severity: "warning" });
      fetchSubscriptions();
    } catch (err) {
      console.error("Silme hatası:", err);
      setSnackbar({ open: true, message: "Silinemedi!", severity: "error" });
    }
  };

  const handleSubmit = async () => {
    const hasMissingId = (formData.subscriptions || []).some((s) => !s.packageId);
    if (hasMissingId) {
      setSnackbar({ open: true, message: "Tüm paketler için packageId zorunludur.", severity: "error" });
      return;
    }

    try {
      // 1) Subscription doc save
      if (formData._id) {
        await axios.put(`/api/subscriptions/${formData._id}`, formData, {
          headers: getAuthHeaders(),
        });
        setSnackbar({ open: true, message: "Abonelik güncellendi!", severity: "success" });
      } else {
        await axios.post(`/api/subscriptions`, formData, {
          headers: getAuthHeaders(),
        });
        setSnackbar({ open: true, message: "Abonelik eklendi!", severity: "success" });
      }

      // 2) Quota save (only existing doc edits)
      if (formData._id && hasQuotaChanges) {
        const diffs = buildQuotaDiffs(quotaItems, quotaOriginal);

        if (diffs.length > 0) {
          await Promise.all(
            diffs.map((d) =>
              axios.patch(
                `/api/subscriptions/${formData._id}/items/${d.itemId}/quotas/${d.quotaType}`,
                d.payload,
                {
                  headers: getAuthHeaders(),
                }
              )
            )
          );
        }

        await fetchQuotaItems(formData._id);
        setSnackbar({ open: true, message: "Kota değişiklikleri kaydedildi", severity: "success" });
        setHasQuotaChanges(false);
      }

      setOpen(false);
      handleClose();
      fetchSubscriptions();
    } catch (error) {
      console.error("Abonelik kaydetme hatası:", error);
      setSnackbar({ open: true, message: "Kayıt başarısız!", severity: "error" });
    }
  };
  // ===============================

  // ========= FILTERS + COUNTS =========
  const filteredRows = abonelikler.filter((row) => {
    const pkgs = row.subscriptions || [];
    const matchesSearch =
      pkgs.some((sub) =>
        String(sub.packageName || "").toLowerCase().includes(searchText.toLowerCase())
      ) || String(row.fullName || "").toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus = statusFilter === "" || String(row.isActive) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const counts = {
    active: abonelikler.filter((a) => a.isActive).length,
    passive: abonelikler.filter((a) => !a.isActive).length,
    total: abonelikler.length,
  };
  // ===================================

  return (
    <Box p={3}>
      {/* Cards */}
      <Box display="flex" justifyContent="center" gap={2} mb={3} flexWrap="wrap">
        {[
          { icon: <DoneAllIcon color="success" />, label: "Aktif Abonelik", value: counts.active },
          { icon: <CancelIcon color="error" />, label: "Pasif Abonelik", value: counts.passive },
          { icon: <PeopleAltIcon color="primary" />, label: "Toplam Abonelik", value: counts.total },
        ].map((card, i) => (
          <Box
            key={i}
            p={2}
            boxShadow={2}
            borderRadius={2}
            bgcolor="white"
            width={250}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            {card.icon}
            <Typography variant="h6" mt={1}>
              {card.label}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Create */}
      <Button
        startIcon={<AddIcon />}
        variant="contained"
        onClick={() => {
          setOpen(true);
          setViewMode(false);
        }}
        sx={{ mb: 2 }}
      >
        Yeni Abonelik Ekle
      </Button>

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          label="Abonelik/Kullanıcı Ara"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Durum</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Durum">
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="true">Aktif</MenuItem>
            <MenuItem value="false">Pasif</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Grid */}
      <Box sx={{ width: "97%", height: 420, mb: 2 }}>
        <DataGrid
          rows={filteredRows.map((row) => ({ id: row._id, ...row }))}
          columns={[
            {
              field: "fullName",
              headerName: "Kullanıcı",
              flex: 1,
              renderCell: (params) => (
                <Box display="flex" flexDirection="column" height={50} justifyContent="center">
                  <Typography variant="body2" fontWeight={600}>
                    {params.row.fullName || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {params.row.userId}
                  </Typography>
                </Box>
              ),
            },
            {
              field: "packages",
              headerName: "Tüm Paketler",
              flex: 1,
              renderCell: (params) => {
                const subs = params.row.subscriptions || [];
                if (subs.length === 0) {
                  return (
                    <Box sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">Yok</Typography>
                    </Box>
                  );
                }

                const firstName = subs[0]?.packageName || "—";
                const extra = subs.length - 1;
                const label = extra > 0 ? `${firstName} +${extra}` : firstName;

                return (
                  <Box sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box>
                          {subs.map((s) => (
                            <Typography key={s._id || s.packageName} variant="body2">
                              • {s.packageName || "—"}
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderRadius: "14px",
                          px: 1.5,
                          py: 0.75,
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          lineHeight: 1,
                          maxWidth: 220,
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {label}
                      </Box>
                    </Tooltip>
                  </Box>
                );
              },
            },
            {
              field: "validity",
              headerName: "Geçerlilik Tarihleri",
              flex: 1,
              renderCell: (params) => {
                const subs = params.row.subscriptions || [];
                if (subs.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  );
                }

                const withDates = subs
                  .map((s) => ({
                    name: s.packageName || "—",
                    startDate: s.startDate ? new Date(s.startDate) : null,
                    endDate: s.endDate ? new Date(s.endDate) : null,
                    isActive: s.isActive,
                    _id: s._id,
                  }))
                  .sort((a, b) => {
                    if (!a.endDate && !b.endDate) return 0;
                    if (!a.endDate) return 1;
                    if (!b.endDate) return -1;
                    return a.endDate - b.endDate;
                  });

                const first = withDates[0];
                const extraCount = withDates.length - 1;

                const summarizeDateRange = (item) => {
                  const startStr = item.startDate ? item.startDate.toLocaleDateString("tr-TR") : "—";
                  const endStr = item.endDate ? item.endDate.toLocaleDateString("tr-TR") : "—";
                  return `${startStr} → ${endStr}`;
                };

                const chipLabel = first
                  ? extraCount > 0
                    ? `${summarizeDateRange(first)} +${extraCount}`
                    : summarizeDateRange(first)
                  : "—";

                const tooltipContent = (
                  <Box>
                    {withDates.map((item) => (
                      <Typography key={item._id || item.name} variant="body2">
                        • {item.name}: {summarizeDateRange(item)}
                        {item.isActive ? " (aktif)" : ""}
                      </Typography>
                    ))}
                  </Box>
                );

                return (
                  <Tooltip arrow placement="top" title={tooltipContent}>
                    <Box display="inline-flex" flexDirection="column" maxWidth="100%">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          bgcolor: "grey.200",
                          color: "text.primary",
                          borderRadius: "16px",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "0.70rem",
                          fontWeight: 500,
                          lineHeight: 1.2,
                          maxWidth: "100%",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {chipLabel}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              },
            },
            {
              field: "isActive",
              headerName: "Durum",
              flex: 0.6,
              renderCell: (params) => (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: params.value ? "green" : "gray" }}
                  >
                    {params.value ? "Aktif" : "Pasif"}
                  </Typography>
                </Box>
              ),
            },
            {
              field: "toggle",
              headerName: "Aktif/Pasif",
              flex: 0.7,
              renderCell: (params) => (
                <Button
                  size="small"
                  variant="outlined"
                  color={params.row.isActive ? "warning" : "success"}
                  onClick={() => handleToggleStatus(params.row)}
                >
                  {params.row.isActive ? "Pasif" : "Aktif"}
                </Button>
              ),
            },
            {
              field: "actions",
              headerName: "İşlemler",
              flex: 0.8,
              renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={1} height={50}>
                  <Tooltip title="Görüntüle">
                    <IconButton onClick={() => handleView(params.row)} size="small">
                      <VisibilityIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Düzenle">
                    <IconButton onClick={() => handleEdit(params.row)} size="small">
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Sil">
                    <IconButton onClick={() => handleDelete(params.row)} color="error" size="small">
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            },
          ]}
          pageSize={5}
        />
      </Box>

      {/* Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionProps={{ onExited: handleClose }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {viewMode ? "Abonelik Detayı" : formData._id ? "Aboneliği Düzenle" : "Yeni Abonelik Ekle"}
          <IconButton onClick={() => setOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* User */}
          <FormControl fullWidth margin="dense">
            <Autocomplete
              disabled={viewMode}
              options={users.filter((u) => u.role === "user")}
              getOptionLabel={(option) => option.fullName || option.id || ""}
              onChange={(e, value) =>
                setFormData((p) => ({
                  ...p,
                  userId: value?.id || "",
                  fullName: value?.fullName || "",
                }))
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Avatar src={option.avatar} sx={{ width: 24, height: 24, mr: 1 }} />
                  {option.fullName || option.id}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Kullanıcı" placeholder="Ad veya soyad ara..." />
              )}
              value={users.find((u) => u.id === formData.userId) || null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </FormControl>

          {/* Packages */}
          <Typography variant="subtitle1" mt={2} mb={1}>
            Paketler
          </Typography>

          {(formData.subscriptions || []).map((sub, index) => (
            <Box key={sub._id || index} mb={2}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Paket Seç</InputLabel>
                <Select
                  value={sub.packageName || ""}
                  onChange={(e) => handleSubscriptionChange(index, "packageName", e.target.value)}
                  label="Paket Seç"
                  disabled={viewMode}
                >
                  {packages.map((pkg) => (
                    <MenuItem key={pkg._id} value={pkg.name}>
                      <Box display="flex" justifyContent="space-between" width="100%" alignItems="center" gap={2}>
                        <Typography variant="body2" fontWeight={600}>
                          {pkg.name}
                        </Typography>
                        <Tooltip title={packageBotsLabel(pkg)} arrow placement="left">
                          <Typography variant="caption" color="text.secondary">
                            {Array.isArray(pkg.bots) ? `${pkg.bots.length} bot` : "0 bot"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>

                {(() => {
                  const selectedPkg = packages.find((p) => p.name === sub.packageName);
                  if (!selectedPkg) return null;
                  const bots = Array.isArray(selectedPkg.bots) ? selectedPkg.bots : [];
                  const names = bots.map((b) => (typeof b === "object" ? (b.name || b._id) : b));
                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Botlar: {names.length ? names.join(", ") : "—"}
                    </Typography>
                  );
                })()}
              </FormControl>

              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  label="Başlangıç Tarihi"
                  type="date"
                  value={sub.startDate || ""}
                  onChange={(e) => handleSubscriptionChange(index, "startDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                  disabled={viewMode}
                />

                <TextField
                  label="Bitiş Tarihi"
                  type="date"
                  value={sub.endDate || ""}
                  onChange={(e) => handleSubscriptionChange(index, "endDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                  disabled={viewMode}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={!!sub.isActive}
                      disabled={viewMode}
                      onChange={(e) => {
                        const updated = [...formData.subscriptions];
                        updated[index] = { ...updated[index], isActive: e.target.checked };
                        setFormData((p) => ({ ...p, subscriptions: updated }));
                      }}
                    />
                  }
                  label={sub.isActive ? "Aktif" : "Pasif"}
                  sx={{ mt: 1 }}
                />
              </Box>

              {!viewMode && formData.subscriptions.length > 1 && (
                <Button onClick={() => removeSubscriptionField(index)} color="error" size="small" sx={{ mt: 1 }}>
                  Paketi Kaldır
                </Button>
              )}

              <Divider sx={{ my: 2 }} />
            </Box>
          ))}

          {!viewMode && (
            <Button onClick={addSubscriptionField} variant="outlined" size="small" sx={{ mt: 1 }}>
              + Yeni Paket Ekle
            </Button>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={!!formData.isActive}
                disabled={viewMode}
                onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
              />
            }
            label={formData.isActive ? "Abonelik Aktif" : "Abonelik Pasif"}
            sx={{ display: "block", mt: 1 }}
          />

          {/* Quotas */}
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Kota / Sayaçlar
            </Typography>

            {quotaLoading ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Yükleniyor…</Typography>
              </Box>
            ) : quotaItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Bu aboneliğe bağlı kota bulunamadı.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.1fr 0.9fr 0.9fr 0.9fr 0.9fr 1.2fr",
                    gap: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  <div>Paket • Quota</div>
                  <div>Tip</div>
                  <div>Limit</div>
                  <div>Kullanılan</div>
                  <div>Kalan</div>
                  <div>İşlem</div>
                </Box>

                {quotaItems.map((row) => {
                  const isCount = row.limitType === "COUNT";

                  return (
                    <Box
                      key={row.rowId}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.1fr 0.9fr 0.9fr 0.9fr 0.9fr 1.2fr",
                        gap: 1,
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {(row.packageName || "—")} • {quotaLabel(row.quotaType)}
                      </Typography>

                      <Select
                        size="small"
                        value={row.limitType}
                        disabled={viewMode}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQuotaItems((prev) =>
                            prev.map((x) => (x.rowId === row.rowId ? { ...x, limitType: val } : x))
                          );
                          setHasQuotaChanges(true);
                        }}
                      >
                        <MenuItem value="COUNT">COUNT</MenuItem>
                        <MenuItem value="MINUTES">MINUTES</MenuItem>
                      </Select>

                      <TextField
                        size="small"
                        type="number"
                        value={row.limitValue ?? 0}
                        disabled={viewMode}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setQuotaItems((prev) =>
                            prev.map((x) => (x.rowId === row.rowId ? { ...x, limitValue: val } : x))
                          );
                          setHasQuotaChanges(true);
                        }}
                      />

                      {isCount ? (
                        <TextField
                          size="small"
                          type="number"
                          value={row.usedCount ?? 0}
                          disabled={viewMode}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setQuotaItems((prev) =>
                              prev.map((x) => (x.rowId === row.rowId ? { ...x, usedCount: val } : x))
                            );
                            setHasQuotaChanges(true);
                          }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={row.usedSeconds ?? 0}
                          disabled={viewMode}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setQuotaItems((prev) =>
                              prev.map((x) => (x.rowId === row.rowId ? { ...x, usedSeconds: val } : x))
                            );
                            setHasQuotaChanges(true);
                          }}
                        />
                      )}

                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {computeRemainingText(row)}
                      </Typography>

                      <Box display="flex" gap={1} alignItems="center">
                        {!viewMode && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              resetOneQuota({
                                docId: selectedSubDoc?._id,
                                itemId: row.subItemId,
                                quotaType: row.quotaType,
                              })
                            }
                          >
                            Sıfırla
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Kapat</Button>
          {!viewMode && (
            <Button onClick={handleSubmit} variant="contained">
              Kaydet
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionPage;